/**
 * @description 날짜/시간 관련 유틸리티 함수와 Date 프로토타입 확장을 제공합니다.
 * dayjs 라이브러리를 기반으로 UTC 지원, 타임프레임 계산, 경과 시간 계산 등을 지원합니다.
 */

import type { ConfigType, OpUnitType, QUnitType } from 'dayjs';
import dayjs from 'dayjs';
import type { DurationUnit, Timeframe, TimeframeSuffix } from './interface';
import utc from 'dayjs/plugin/utc.js';
import duration from 'dayjs/plugin/duration.js';
import weekOfYear from 'dayjs/plugin/weekOfYear.js';
import updateLocale from 'dayjs/plugin/updateLocale.js';

/**
 * 기본 날짜 포맷 문자열
 * @example 'YYYY-MM-DD HH:mm:ss' → '2024-01-15 14:30:00'
 */
export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

// const DURATION_UNIT_INDEXES = DURATION_UNITS.reduce(
//   (acc, unit, index) => {
//     acc[unit] = index;
//     return acc;
//   },
//   {} as Record<DurationUnit, number>,
// );

// UTC 지원
dayjs.extend(utc);
// 주 지원
dayjs.extend(weekOfYear);
// 일 계산에 필요
dayjs.extend(duration);
// locale 업데이트 지원
dayjs.extend(updateLocale);

// dayjs 기본값은 일요일(0)을 주의 시작으로 인식하는데,
// 바이낸스는 월요일(1)을 주의 시작으로 사용하므로 dayjs의 기본 locale을 업데이트
// dayjs의 기본 로케일이 'en'이라서 여기서 설정
dayjs.updateLocale('en', { weekStart: 1 });
dayjs.updateLocale('ko', { weekStart: 1 });

/** Timeframe 문자열의 마지막 글자를 TimeframeSuffix로 추출 */
function extractSuffix(timeframe: Timeframe): TimeframeSuffix {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return timeframe.slice(-1) as TimeframeSuffix;
}

declare global {
  interface Date {
    /**
     * 두 시간의 차이를 지정한 인터벌 단위로 반환. unit 누락 시 기본 밀리세컨드
     * dt가 미래이면 양수를 반환
     * @param dt
     * @param unit
     * @returns number
     */
    diff: (dt: ConfigType, unit?: QUnitType | OpUnitType) => number;
    /**
     * 지정한 형식으로 반환
     * @param fmt dayjs format
     */
    format: (fmt?: string) => string;
  }
}

Date.prototype.diff = function (dt: ConfigType, unit?: QUnitType | OpUnitType): number {
  return dayjs(this).diff(dt, unit);
};

/**
 * timestamp 또는 Date 객체를 지정한 형식으로 반환
 * @param value timestamp 또는 Date 객체
 * @param fmt dayjs format
 * @returns
 */
export const dateFormat = (
  value:
    | number
    | Date
    | {
        valueOf(): number | Date;
      },
  fmt = DEFAULT_DATE_FORMAT,
) => {
  const val = typeof value === 'object' ? value.valueOf() : value;
  return dayjs.utc(val).local().format(fmt);
};

/**
 * Date 객체를 지정한 형식으로 반환
 * @param fmt dayjs format
 * @returns
 */
Date.prototype.format = function (fmt = DEFAULT_DATE_FORMAT): string {
  return dateFormat(this, fmt);
};

/**
 * 타임프레임 문자열을 숫자 간격과 단위로 분리합니다.
 *
 * @param timeframe - 타임프레임 문자열 (예: '5m', '1h', '4h')
 * @returns `{ interval, unit }` 객체
 *
 * @example
 * ```ts
 * getTimeframeIntervalUnit('5m'); // { interval: 5, unit: 'm' }
 * getTimeframeIntervalUnit('4h'); // { interval: 4, unit: 'h' }
 * ```
 */
export function getTimeframeIntervalUnit(timeframe: Timeframe) {
  const unit = extractSuffix(timeframe); // 맨뒤의 한 글자
  const interval = Number(timeframe.replace(unit, ''));
  return { interval, unit };
}

/**
 * 주어진 타임스탬프가 속한 타임프레임의 시작 시간을 반환합니다.
 *
 * @param timestamp - 밀리초 단위 타임스탬프
 * @param timeframe - 타임프레임 (예: '5m', '1h', '1d')
 * @returns 해당 타임프레임 봉의 시작 시간 (밀리초)
 *
 * @example
 * ```ts
 * // 2024-01-15 14:37:00 → 2024-01-15 14:35:00 (5분봉 시작)
 * getStartOfTimeframe(1705326420000, '5m');
 * ```
 */
export function getStartOfTimeframe(timestamp: number, timeframe: Timeframe): number {
  let dj = dayjs.utc(timestamp);
  const { interval, unit } = getTimeframeIntervalUnit(timeframe);

  if (unit === 'm') {
    const val = Math.floor(dj.minute() / interval) * interval;
    dj = dj.minute(val).startOf(unit);
  } else if (unit === 'h') {
    const val = Math.floor(dj.hour() / interval) * interval;
    dj = dj.hour(val).startOf(unit);
  } else if (unit === 'w') {
    const val = Math.floor(dj.week() / interval) * interval;
    dj = dj.week(val).startOf(unit);
  } else if (unit === 'M') {
    const val = Math.floor(dj.month() / interval) * interval;
    dj = dj.month(val).startOf(unit);

    // 이하는 1970년 이후 unix time 기준으로 단위를 누적시켜서 계산
  } else if (unit === 'd') {
    const du = dayjs.duration(dj.diff(dayjs.utc(0)));
    const days = (Math.floor(du.asDays()) / interval) * interval;
    dj = dayjs.utc(0).add(dayjs.duration({ days }));
  }
  return dj.valueOf();
}

/**
 * 주어진 시간이 특정 타임프레임의 봉 시작 시점인지 확인합니다.
 *
 * @param dt - 확인할 Date 객체
 * @param timeframe - 타임프레임 (예: '5m', '1h', '1d')
 * @returns 타임프레임 시작 시점이면 true
 *
 * @example
 * ```ts
 * isTimeframeTime(new Date('2024-01-15 14:35:00'), '5m'); // true
 * isTimeframeTime(new Date('2024-01-15 14:37:00'), '5m'); // false
 * ```
 */
export function isTimeframeTime(dt: Date, timeframe: Timeframe) {
  const dj = dayjs.utc(dt);
  const day = dj.day();
  const hour = dj.hour();
  const minute = dj.minute();
  const week = dj.week();
  const month = dj.month();

  const suffix = extractSuffix(timeframe); // 맨뒤의 한 글자

  if (suffix === 'm') {
    // 몇 분 간격
    if (minute % Number(timeframe.replace('m', '')) === 0) {
      return true;
    }
  } else if (suffix === 'h') {
    // 몇시간 간격 and 0분
    if (hour % Number(timeframe.replace('h', '')) === 0 && minute === 0) {
      return true;
    }
  } else if (suffix === 'd') {
    // 몇일 간격, 0시, 0분마다
    if (day % Number(timeframe.replace('d', '')) === 0 && hour === 0 && minute === 0) {
      return true;
    }
  } else if (suffix === 'w') {
    // Date.getDay()가 0이면 일요일
    // 몇주 간격, 일요일, 0시, 0분마다
    if (week % Number(timeframe.replace('w', '')) === 0 && dt.getDay() === 0 && hour === 0 && minute === 0) {
      return true;
    }
  } else if (suffix === 'M') {
    // 몇월 간격, 1일, 0시, 0분마다
    if (month % Number(timeframe.replace('M', '')) === 0 && day === 1 && hour === 0 && minute === 0) {
      return true;
    }
  }
  return false;
}

/**
 * 과거시간(fromTime)에서 나중시간(toTime) 사이 봉단위 간격 개수 구하기
 * @param fromTime
 * @param toTime 누락 시 현재 시간 사용
 * @returns number | undefined
 * @example
 * const diff = getTimeframeDiffCount('5m', new Date('2024-08-21 12:10:00').valueOf(), new Date('2024-08-21 12:21:30').valueOf());
 * console.log(diff);
  // 2 출력
 */
export function getTimeframeDiffCount(timeframe: Timeframe, fromTime: number, toTime?: number): number {
  const toDt = toTime ? dayjs.utc(toTime) : dayjs.utc();
  const fromDt = dayjs.utc(fromTime);
  const suffix = extractSuffix(timeframe); // 맨뒤의 한 글자
  const interval = Number(timeframe.replace(suffix, ''));
  const count = toDt.diff(fromDt, suffix);

  // timeframe이 5m 이고, 경과한 분이 11분이라면, 11 / 5 내림한 값에 여유로 2개 더 추가하여 가져오자.
  // timeframe이 4h 이고, 경과한 시간이 5시간이라면, 5 / 4 내림한 값에 여유로 2개 더 추가
  return Math.floor(count / interval);
}

/**
 * 타임프레임을 밀리초 단위로 환산합니다.
 *
 * @param timeframe - 타임프레임 (예: '5m', '1h', '1d')
 * @returns 밀리초 단위 시간
 *
 * @example
 * ```ts
 * getTimeframeMilliseconds('1m'); // 60000
 * getTimeframeMilliseconds('1h'); // 3600000
 * getTimeframeMilliseconds('1d'); // 86400000
 * ```
 */
export const getTimeframeMilliseconds = (timeframe: Timeframe) => {
  const suffix = extractSuffix(timeframe); // 맨뒤의 한 글자
  const count = Number(timeframe.replace(suffix, ''));
  return dayjs.duration(count, suffix).asMilliseconds();
};

/**
 * 경과시간을 일, 시간, 분, 초 단위로 분석하여 반환
 * @param elapsedTime 두 date 객체의 차이로 얻은 밀리세컨드 단위 경과 시간
 * @returns {
 *  years: number,
 *  months: number,
 *  days: number,
 *  hours: number,
 *  minutes: number,
 *  seconds: number,
 *  milliseconds: number
 * } maxUnit 누락 시 년 단위로 반환.
 */
export function getDuration(elapsedTime: number, maxUnit: DurationUnit = 'year') {
  // dayjs의 duration을 사용하여 경과 시간을 분해합니다.
  const dur = dayjs.duration(elapsedTime);

  // 년, 월, 일, 시간, 분, 초, 밀리세컨드초 를 추출합니다.
  const years = dur.years();
  const months = dur.months();
  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();
  const seconds = dur.seconds();
  const milliseconds = dur.milliseconds();

  // 최대 단위까지 반환
  if (maxUnit === 'year') {
    return { years, months, days, hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === 'month') {
    return { years: 0, months: Math.floor(dur.asMonths()), days, hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === 'day') {
    return { years: 0, months: 0, days: Math.floor(dur.asDays()), hours, minutes, seconds, milliseconds };
  }
  if (maxUnit === 'hour') {
    return { years: 0, months: 0, days: 0, hours: Math.floor(dur.asHours()), minutes, seconds, milliseconds };
  }
  if (maxUnit === 'minute') {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: Math.floor(dur.asMinutes()), seconds, milliseconds };
  }
  if (maxUnit === 'second') {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: Math.floor(dur.asSeconds()), milliseconds };
  }
  return {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: Math.floor(dur.asMilliseconds()),
  };
}

/** 경과 분 구하기
 * @param elapsedTime 두 date 객체의 차이로 얻은 밀리세컨드 단위 경과 시간
 */
export const toElapsedMinutes = (elapsedTime: number) => {
  return Math.floor(elapsedTime / 60000);
};

/** 경과 초 구하기
 * @param elapsedTime 두 date 객체의 차이로 얻은 밀리세컨드 단위 경과 시간
 */
export const toElapsedSeconds = (elapsedTime: number) => {
  return Math.floor(elapsedTime / 1000);
};

/** 주어진 시간부터 현재 시간까지 경과 시간을 반환 */
export const getElapsedTime = (dtStart: Date | number, dtEnd?: Date | number | null) => {
  const endTime = dtEnd ? (typeof dtEnd === 'number' ? dtEnd : dtEnd.getTime()) : Date.now();
  const elapsedTime = endTime - (typeof dtStart === 'number' ? dtStart : dtStart.getTime());
  return elapsedTime;
};

/** 주어진 시간부터 현재 시간까지 경과 시간을 분 단위로 반환 */
export const getElapsedMinutes = (dt: Date | number) => {
  return toElapsedMinutes(getElapsedTime(dt));
};

/** 주어진 시간부터 현재 시간까지 경과 시간을 초 단위로 반환 */
export const getElapsedSeconds = (dt: Date | number) => {
  return toElapsedSeconds(getElapsedTime(dt));
};

/**
 * 경과 시간을 지정된 형식의 문자열로 포맷합니다.
 *
 * @param elapsedTime - 밀리초 단위 경과 시간
 * @param formatString - dayjs duration 포맷 문자열 (기본값: 'HH:mm:ss')
 * @returns 포맷된 문자열
 *
 * @example
 * ```ts
 * formatDuration(3661000); // '01:01:01'
 * formatDuration(3661000, 'H시간 m분 s초'); // '1시간 1분 1초'
 * ```
 */
export const formatDuration = (elapsedTime: number, formatString: string = 'HH:mm:ss') => {
  return dayjs.duration(elapsedTime).format(formatString);
};

export {};
